import { useLayoutEffect, useRef, useState, type RefObject } from 'react'

/** Caixa do elemento ativo, em pixels, relativa à caixa de padding do contentor. */
export interface IndicatorBox {
  left: number
  top: number
  width: number
  height: number
}

export interface SlidingIndicator<C extends HTMLElement> {
  containerRef: RefObject<C | null>
  /** `null` enquanto não há nada medível — ver a nota sobre degradação abaixo. */
  box: IndicatorBox | null
}

function sameBox(a: IndicatorBox, b: IndicatorBox): boolean {
  return a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height
}

/**
 * Mede o elemento ativo dentro de um contentor para que UM indicador possa
 * deslizar até ele, em vez de acender no sítio.
 *
 * Existe porque a diferença entre "acender" e "mover" não é decorativa: quando
 * cada opção pinta o próprio fundo, trocar de opção apaga um retângulo e
 * acende outro — dois eventos sem nada que os ligue. Um indicador único que se
 * desloca lê-se como o mesmo objeto mudando de lugar, que é o que de facto
 * aconteceu.
 *
 * QUEM ESTÁ ATIVO É O ATRIBUTO DE ACESSIBILIDADE, passado em `activeSelector`
 * — `aria-pressed` nos filtros, `aria-current` na navegação. Não há estado
 * paralelo em React a dizer quem é o ativo: assim é impossível o indicador
 * apontar para um sítio e o leitor de tela anunciar outro.
 *
 * DEGRADAÇÃO: devolve `null` quando não há layout medível (contentor escondido,
 * ambiente sem motor de layout) ou quando nada está ativo. Quem consome deve,
 * nesse caso, deixar o estado pintado no próprio elemento — o indicador é a
 * forma do movimento, nunca a única maneira de saber o que está selecionado.
 *
 * @param activeSelector seletor CSS do elemento ativo, dentro do contentor
 * @param key muda quando a seleção ou o conteúdo muda — é o que dispara a
 *   remedição (o valor do filtro, o pathname da rota)
 */
export function useSlidingIndicator<C extends HTMLElement>(
  activeSelector: string,
  key: string,
): SlidingIndicator<C> {
  const containerRef = useRef<C>(null)
  const [box, setBox] = useState<IndicatorBox | null>(null)

  useLayoutEffect(() => {
    const container = containerRef.current
    const active = container?.querySelector<HTMLElement>(activeSelector) ?? null
    if (!container || !active) {
      // Nada ativo: o indicador tem de sair, senão fica a apontar para o item
      // anterior (o caso de navegar para uma rota fora do menu).
      setBox(null)
      return
    }

    const measure = (): void => {
      const next: IndicatorBox = {
        left: active.offsetLeft,
        top: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight,
      }
      // Zero significa "ainda não há layout" — não é uma medida, é a ausência
      // de uma. Guardá-la poria o indicador a colapsar em cima de nada.
      if (next.width === 0 || next.height === 0) return
      setBox((prev) => (prev && sameBox(prev, next) ? prev : next))
    }

    measure()

    // Sem observador o indicador ficaria preso à geometria da primeira pintura:
    // contagens chegam depois do primeiro render e alargam o alvo, e a janela
    // pode ser redimensionada.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    observer.observe(active)
    return () => observer.disconnect()
  }, [activeSelector, key])

  return { containerRef, box }
}
